import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Skeleton,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { useCombobox } from "downshift";
import { useField, useFormikContext } from "formik";
import { FormControl } from "formik-chakra-ui";
import _ from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { RiAddLine, RiCloseLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { Glossary } from "src/components/Text/Glossary";
import { useSearchApplicants } from "src/generated/hooks/form-service/queries";
import type { ApplicantSummary } from "src/generated/hooks/form-service/requests";
import { useFocus } from "src/hooks/useFocus";
import { useOrganization } from "src/hooks/useOrganization";
import { parseName } from "src/services/format";
import * as Url from "src/services/url";

const PAGE_SIZE = 5;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;

interface EligibleStudentSelectInputProps {
  name: string;
  organizationId: string;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onSelectedStudent?: (value: ApplicantSummary | null | undefined) => void;
  // When set, the create-new flow carries this context so the create-student page
  // can return the user to the forms list with the create-form dialog reopened
  // (and, on Back, reopened with the original search text).
  createNewStudentReturn?: {
    formTemplateId: string;
    enrollmentPeriodId: string;
    params: string;
  };
  // Invoked right before the create-new navigation. A host dialog passes its close
  // handler here so it does not stay frozen-open (which would keep Chakra's modal
  // scroll lock active on the destination page and reopen the dialog on return).
  onCreateNewStudent?: () => void;
  // Whether the create-new-student row is shown. Per PR-1 it only appears for staff
  // who can create a student (user:create); hidden otherwise (no-match stays as-is).
  canCreateStudent?: boolean;
  // Seed a preselected student (e.g. one just created via the Add family flow)
  // without a search round-trip.
  initialSelectedStudent?: ApplicantSummary | null;
  // Seed the search input text (e.g. restoring the original search on Back).
  initialSearchText?: string;
}

function toFullName(
  person: ApplicantSummary | null | undefined,
  defaultValue: string = ""
): string {
  if (!person) return defaultValue;
  const parts = [
    person.first_name,
    person.middle_name,
    person.last_name,
  ].filter(Boolean);
  return parts.join(" ") || defaultValue;
}

export function EligibleStudentSelectInput({
  name,
  organizationId,
  onSelectedStudent,
  placeholder = "Search for student by name or ID",
  label = "Student",
  isDisabled = false,
  isRequired = false,
  createNewStudentReturn,
  onCreateNewStudent,
  canCreateStudent = true,
  initialSelectedStudent,
  initialSearchText,
}: EligibleStudentSelectInputProps) {
  const navigate = useNavigate();
  const organization = useOrganization();

  const formik = useFormikContext();
  const [field, { initialValue }, helper] = useField(name);
  const [hasUserSelected, setHasUserSelected] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [applicants, setApplicants] = React.useState<ApplicantSummary[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const [selectedStudent, setSelectedStudent] = React.useState<
    ApplicantSummary | null | undefined
  >(undefined);

  const searchValue = inputValue.trim();

  const [debouncedSearchValue, setDebouncedSearchValue] =
    React.useState(searchValue);
  const debouncedSetSearchValue = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearchValue(searchValue);
    return () => debouncedSetSearchValue.cancel();
  }, [searchValue, debouncedSetSearchValue]);

  const {
    data: searchResponse,
    isLoading,
    isError: isSearchError,
  } = useSearchApplicants(
    {
      query: {
        organizationId,
        search: debouncedSearchValue,
        limit: PAGE_SIZE,
        offset,
      },
    },
    undefined,
    {
      enabled:
        !!organizationId && debouncedSearchValue.length >= MIN_SEARCH_LENGTH,
    }
  );
  const data = searchResponse?.data;

  React.useEffect(() => {
    if (debouncedSearchValue.length < MIN_SEARCH_LENGTH) {
      setApplicants([]);
      setTotal(0);
      setOffset(0);
      return;
    }
    if (!data) return;
    if (offset === 0) {
      setApplicants(data.applicants);
    } else {
      setApplicants((prev) => _.unionBy(prev, data.applicants, "id"));
    }
    setTotal(data.total);
  }, [data, offset, debouncedSearchValue]);

  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearchValue]);

  React.useEffect(() => {
    if (!hasUserSelected && applicants.length > 0 && initialValue) {
      const preSelected = applicants.find((s) => s.id === initialValue);
      if (preSelected) {
        setSelectedStudent(preSelected);
        onSelectedStudent?.(preSelected);
        setInputValue(toFullName(preSelected));
        setOffset(0);
      }
    }
  }, [applicants, hasUserSelected, initialValue, onSelectedStudent]);

  const fetchMore = React.useCallback(() => {
    if (applicants.length < total && searchValue.length >= MIN_SEARCH_LENGTH) {
      setOffset(applicants.length);
    }
  }, [applicants.length, total, searchValue]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoading && applicants.length > 0) {
          fetchMore();
        }
      },
      { rootMargin: "20px" }
    );
    const current = observerRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [applicants, isLoading, fetchMore]);

  const [isFocused, setIsFocused] = React.useState(false);
  const [inputRef, setInputFocus] = useFocus();
  React.useEffect(() => {
    if (isFocused) setInputFocus();
  }, [setInputFocus, isFocused]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.onblur = () => setIsFocused(false);
    }
  }, [inputRef, isFocused]);

  const resetField = () => {
    helper.setTouched(true, false);
    helper.setValue("");
    setInputValue("");
    setApplicants([]);
    setSelectedStudent(null);
    onSelectedStudent?.(null);
    setOffset(0);
    setIsFocused(true);
  };

  // Seed a preselected student or the restored search text on mount, when
  // returning from the Add family flow. The dialog remounts this input per open,
  // so a mount-only effect seeds exactly once.
  React.useEffect(() => {
    if (initialSelectedStudent) {
      setHasUserSelected(true);
      setSelectedStudent(initialSelectedStudent);
      setInputValue(toFullName(initialSelectedStudent));
      onSelectedStudent?.(initialSelectedStudent);
    } else if (initialSearchText) {
      setInputValue(initialSearchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateNewStudent = () => {
    closeMenu();
    // Close the host dialog first so it is not frozen open during navigation.
    onCreateNewStudent?.();
    // Carry the prefill and (when coming from the form dialog) the return context
    // in the URL rather than navigation state, so it survives a page reload.
    // Mirrors Url.OrgAdmin.Messages.new(org, params).
    const query = new URLSearchParams();
    const { firstName, lastName } = parseName(inputValue);
    if (firstName) query.set("prefillFirstName", firstName);
    if (lastName) query.set("prefillLastName", lastName);
    if (createNewStudentReturn) {
      query.set("returnFormTemplateId", createNewStudentReturn.formTemplateId);
      query.set("returnParams", createNewStudentReturn.params);
      query.set("returnSearchText", inputValue);
    }
    const destination = organization
      .map((org) => Url.OrgAdmin.Students.new(org, query.toString()))
      .withDefault("#");
    // Defer navigation a tick so the dialog's close commits (releasing Chakra's
    // modal scroll lock) before PreserveState freezes the forms page; otherwise
    // the freeze snapshots the open modal and its scroll lock persists on the
    // destination page. Mirrors useCreateFormForTemplateDialog.navigateToForm.
    setTimeout(() => navigate(destination), 0);
  };

  const handleChange = (value: string | null) => {
    const id = value ?? "";
    // Skip only when this student is already the visible selection. Guarding on
    // field.value alone drops the selection when Formik still holds a stale id but
    // nothing is displayed (e.g. after the picker remounts).
    if (id === field.value && selectedStudent?.id === id) return;
    setHasUserSelected(true);
    const found = applicants.find((s) => s.id === id) ?? null;
    setSelectedStudent(found);
    onSelectedStudent?.(found);
    helper.setTouched(true, false);
    helper.setValue(id);
    // Defer so Formik has applied setValue before we re-run validation
    setTimeout(() => {
      void formik.validateField(name);
    }, 0);
  };

  const noResults =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH &&
    !isLoading &&
    data !== undefined &&
    data.total === 0;

  // The create-new action only surfaces once a search has actually resolved for
  // the current query (results or no results), never while idle or loading.
  const searchAttempted =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH &&
    !isLoading &&
    data !== undefined;

  // Below the minimum search length (including an empty, focused field) no search
  // fires — show a hint rather than an empty dropdown.
  const showSearchHint = debouncedSearchValue.length < MIN_SEARCH_LENGTH;

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setHighlightedIndex,
  } = useCombobox<ApplicantSummary>({
    items: applicants,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: toFullName,
    onInputValueChange({ inputValue: newValue }) {
      if (newValue !== inputValue) {
        setInputValue(newValue ?? "");
        setOffset(0);
      }
    },
    stateReducer(state, actionAndChanges) {
      const { changes, type, index } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const newIndex =
            index !== undefined && index >= 0
              ? index
              : changes.highlightedIndex !== undefined &&
                changes.highlightedIndex >= 0
              ? changes.highlightedIndex
              : undefined;
          const newSelectedItem =
            newIndex !== undefined ? applicants[newIndex] : null;
          return { ...changes, selectedItem: newSelectedItem };
        }
        default:
          return changes;
      }
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) handleChange(selectedItem.id);
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) openMenu();
  }, [isDisabled, openMenu]);

  const markdownName = React.useCallback(
    (displayName: string) => {
      if (!inputValue) return displayName;
      const escaped = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "ig");
      return displayName.replace(regex, "**$1**");
    },
    [inputValue]
  );

  const renderItem = (applicant: ApplicantSummary) => {
    const displayName = toFullName(applicant);
    const name = markdownName(displayName);
    return (
      <Flex direction="column" gap={0} width="100%">
        <Flex direction="row" gap={0} width="100%">
          <Text fontSize="md" marginEnd="auto">
            <Markdown>{name}</Markdown>
          </Text>
          {applicant.birth_date && (
            <Text fontSize="sm">
              Born{" "}
              {DateFns.format(
                DateFns.parse(applicant.birth_date, "yyyy-MM-dd", new Date()),
                "MM/dd/yyyy"
              )}
            </Text>
          )}
        </Flex>
        <Text fontSize="sm" overflow="visible">
          ID: {applicant.id}
        </Text>
        {applicant.reference_id && (
          <Text fontSize="sm">Reference ID: {applicant.reference_id}</Text>
        )}
      </Flex>
    );
  };

  const renderLoadingItem = () => (
    <Flex direction="column" gap={0} width="100%">
      <Flex direction="row" gap={0} width="100%">
        <Skeleton height="1.25rem" width="60%" marginEnd="auto" />
        <Skeleton height="1rem" width="30%" />
      </Flex>
      <Skeleton height="1rem" width="40%" marginTop="0.5rem" />
    </Flex>
  );

  return (
    <Box position="relative" width="100%">
      <FormControl name={name} isRequired={isRequired}>
        <FormLabel {...getLabelProps()} htmlFor={name}>
          <Glossary>{label}</Glossary>
        </FormLabel>
        {selectedStudent ? (
          <Flex
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            paddingLeft={4}
            paddingRight={3}
            paddingTop={2}
            paddingBottom={2}
            gap={2.5}
            direction="row"
          >
            {renderItem(selectedStudent)}
            {!isDisabled && (
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                p="0"
                onClick={resetField}
              >
                <RiCloseLine />
              </Button>
            )}
          </Flex>
        ) : (
          <Popover
            isOpen={isOpen}
            onClose={closeMenu}
            autoFocus={false}
            closeOnBlur={false}
            returnFocusOnClose={false}
            matchWidth
            placement="bottom-start"
            isLazy
          >
            <PopoverAnchor>
              <InputGroup
                onClick={openMenuUnlessDisabled}
                onFocus={openMenuUnlessDisabled}
                display="flex"
                justifyContent="center"
              >
                <InputLeftElement children={<SearchIcon color="gray.500" />} />
                <Input
                  {...getInputProps(
                    {
                      onBlur: () => {
                        if (hasUserSelected || inputValue.length > 0) {
                          helper.setTouched(true);
                        }
                      },
                      ref: inputRef,
                    },
                    { suppressRefError: true }
                  )}
                  value={inputValue}
                  placeholder={placeholder}
                  isDisabled={isDisabled}
                  required={false}
                />
              </InputGroup>
            </PopoverAnchor>
            {/* Not portaled to body: rendered within the create-form modal's
                stacking context so it layers above the dialog instead of behind
                it. The student phase doesn't inner-scroll, so it isn't clipped. */}
            <PopoverContent
              width="auto"
              boxShadow="lg"
              _focusVisible={{ outline: "none" }}
            >
              <UnorderedList
                {...getMenuProps({}, { suppressRefError: true })}
                margin={0}
                overflow="auto"
                maxHeight="300px"
              >
                {showSearchHint && (
                  <ListItem textAlign="center" p={3}>
                    <Text color="gray.500" fontSize="md">
                      Enter a name or ID to search.
                    </Text>
                  </ListItem>
                )}
                {!noResults &&
                  !isLoading &&
                  applicants.map((applicant, index) => (
                    <ListItem
                      {...getItemProps({ item: applicant, index })}
                      key={applicant.id}
                      backgroundColor={
                        highlightedIndex === index ? "blackAlpha.100" : "white"
                      }
                      listStyleType="none"
                    >
                      <Flex align="center" minHeight="16" padding="4">
                        {renderItem(applicant)}
                      </Flex>
                    </ListItem>
                  ))}
                {noResults && !isSearchError && (
                  <ListItem textAlign="center" p={3}>
                    <Text color="gray.500" fontSize="md">
                      <Glossary>No eligible students found</Glossary>
                    </Text>
                  </ListItem>
                )}
                {isSearchError && !isLoading && (
                  <ListItem textAlign="center" p={3}>
                    <Text color="red.600" fontSize="md">
                      Something went wrong searching for students. Please try
                      again.
                    </Text>
                  </ListItem>
                )}
                {isLoading && (
                  <ListItem textAlign="center" p={2}>
                    <Flex align="center" minHeight="16" padding="4">
                      {renderLoadingItem()}
                    </Flex>
                  </ListItem>
                )}
                <Box ref={observerRef} height="5px" />
              </UnorderedList>
              {/* The create action is outside the scrolling list so paging can
                  never push it out of reach and its content is not clipped. */}
              {searchAttempted && !isSearchError && canCreateStudent && (
                <Flex
                  borderTop="1px solid"
                  borderColor="gray.100"
                  align="center"
                  gap={2}
                  padding="4"
                  color="primary.500"
                  cursor="pointer"
                  _hover={{ backgroundColor: "blackAlpha.100" }}
                  // Keep input focus so the click isn't lost to a blur-close.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setHighlightedIndex(-1)}
                  onClick={handleCreateNewStudent}
                >
                  <RiAddLine size="1.25rem" />
                  <Text fontSize="md" fontWeight="700">
                    <Glossary>Create new student</Glossary> "{inputValue}"
                  </Text>
                </Flex>
              )}
            </PopoverContent>
          </Popover>
        )}
      </FormControl>
    </Box>
  );
}
